import { DeleteOutlined, EditOutlined, MoreOutlined } from '@ant-design/icons'
import type { Carer } from '@lmc/ts-common'
import type { MenuProps } from 'antd'
import { Dropdown } from 'antd'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

type RowActionsMenuProps = {
  carer: Carer
  onEditPermissions: (carer: Carer) => void
  onDeleteAccount: (carer: Carer) => void
  // Each action is gated on its own CASL ability: Edit on `update User`, Delete
  // on `delete User`. A viewer who can edit but not delete must not see Delete.
  canEditPermissions?: boolean
  canDeleteAccount?: boolean
  testId?: string
}

const StyledTrigger = styled.button`
  appearance: none;
  background: ${(props) => props.theme.palette.white};
  border: 1px solid ${(props) => props.theme.color.neutral.color6};
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${(props) => props.theme.color.neutral.color3};

  &:hover {
    color: ${(props) => props.theme.color.hover.primary};
    border-color: ${(props) => props.theme.color.hover.primary};
  }

  &:focus-visible {
    outline: 2px solid rgba(24, 144, 255, 0.2);
    outline-offset: 1px;
  }
`

const StyledDeleteLabel = styled.span`
  color: ${(props) => props.theme.color.status.danger};
`

const StyledDeleteIcon = styled(DeleteOutlined)`
  color: ${(props) => props.theme.color.status.danger};
`

const RowActionsMenu: React.FC<RowActionsMenuProps> = ({
  carer,
  onEditPermissions,
  onDeleteAccount,
  canEditPermissions = true,
  canDeleteAccount = true,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const items: MenuProps['items'] = [
    canEditPermissions && {
      key: 'edit-permissions',
      label: t(reportsConstants.TEAM_LIST_EDIT_PERMISSIONS),
      icon: <EditOutlined />,
      onClick: () => onEditPermissions(carer)
    },
    canDeleteAccount && {
      key: 'delete-account',
      label: (
        <StyledDeleteLabel
          data-testid={testId ? `${testId}-delete-account` : undefined}
        >
          {t(reportsConstants.TEAM_LIST_DELETE_ACCOUNT)}
        </StyledDeleteLabel>
      ),
      icon: <StyledDeleteIcon />,
      onClick: () => onDeleteAccount(carer)
    }
  ].filter(Boolean) as MenuProps['items']

  return (
    <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
      <StyledTrigger
        type="button"
        aria-label={t(reportsConstants.TEAM_LIST_ROW_ACTIONS_LABEL)}
        aria-haspopup="menu"
        data-testid={testId}
      >
        <MoreOutlined rotate={90} />
      </StyledTrigger>
    </Dropdown>
  )
}

export default RowActionsMenu
